import type {
  Workspace,
  WorkspaceRole,
  WorkspaceStatus,
} from "@/api";
import type { BadgeTone } from "@/components/ui/badge";

/** Mirrors the backend ``MAX_WORKSPACES_PER_TENANT`` constant
 *  (see ``apps/api/src/core/config.py``). Used by the
 *  workspaces list page counter; the backend enforces the limit
 *  authoritatively so this is purely a UX hint. */
export const PILOT_MAX_WORKSPACES_PER_TENANT = 100;

export const WORKSPACE_STATUS_LABEL: Record<WorkspaceStatus | string, string> = {
  active: "Aktif",
  archived: "Diarsipkan",
};

export const WORKSPACE_STATUS_TONE: Record<string, BadgeTone> = {
  active: "success",
  archived: "neutral",
};

export const WORKSPACE_ROLE_LABEL: Record<string, string> = {
  workspace_admin: "Admin Workspace",
  workspace_member: "Anggota",
  workspace_viewer: "Pengamat",
};

export const WORKSPACE_ROLE_TONE: Record<string, BadgeTone> = {
  workspace_admin: "ocean",
  workspace_member: "neutral",
  workspace_viewer: "info",
};

export function formatWorkspaceStatus(status: string): string {
  return WORKSPACE_STATUS_LABEL[status] ?? status;
}

export function formatWorkspaceRole(role: WorkspaceRole | string): string {
  return WORKSPACE_ROLE_LABEL[role] ?? role;
}

/** Slug auto-generation from a display name. Matches the backend
 *  regex ``^[a-z0-9]+(?:-[a-z0-9]+)*$`` — drops accents, lowercase,
 *  collapses non-alphanum to single hyphen, trims edges. */
export function slugifyWorkspaceName(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isValidWorkspaceSlug(slug: string): boolean {
  return slug.length >= 1 && slug.length <= 100 && SLUG_PATTERN.test(slug);
}

/** Archive guardrail — mirrors the backend
 *  ``CannotArchiveDefaultWorkspaceError`` + last-active protection.
 *  Returns ``null`` when archive is allowed, otherwise a Bahasa
 *  Indonesia reason string the FE surfaces in the confirm dialog. */
export function reasonCannotArchive(
  workspace: Workspace,
  allWorkspaces: ReadonlyArray<Workspace>
): string | null {
  if (workspace.is_default) {
    return "Workspace default tidak dapat diarsipkan. Set workspace lain sebagai default terlebih dahulu.";
  }
  const activeOthers = allWorkspaces.filter(
    (w) => w.status === "active" && w.id !== workspace.id
  );
  if (activeOthers.length === 0) {
    return "Minimal harus ada 1 workspace aktif. Buat workspace lain terlebih dahulu.";
  }
  return null;
}

export function isWorkspaceActive(ws: Workspace): boolean {
  return ws.status === "active";
}
